import React, { useState } from 'react';

import '../../styles/landing-page.scss';

export const LandingPageSections = () => {
  return (
    <div className="pseudo-landing-page">
      <CustomerReviews />
      <PricingPlans />
      <FAQSection />
    </div>
  );
};

// Customer Reviews Section
const CustomerReviews = () => {
  const reviews = [
    {
      id: 1,
      name: 'Sarah Chen',
      location: 'Austin, TX',
      rating: 5,
      review: 'Creating my will used to feel overwhelming, but Life Saver made it simple and stress-free. I completed everything in about 20 minutes during my lunch break. The step-by-step guidance was perfect.',
      verified: true
    },
    {
      id: 2,
      name: 'Michael Rodriguez',
      location: 'Denver, CO',
      rating: 5,
      review: 'As a single parent, I needed to ensure my daughter would be taken care of. The guardian selection process was thorough yet easy to understand. I feel so much more secure knowing everything is documented properly.',
      verified: true
    },
    {
      id: 3,
      name: 'Jennifer Park',
      location: 'Seattle, WA',
      rating: 5,
      review: 'I\'ve been putting off making a will for years. Life Saver removed all the barriers - no expensive lawyer visits, no complicated legal jargon. The mobile app made it convenient to complete on my own schedule.',
      verified: true
    },
    {
      id: 4,
      name: 'Robert Thompson',
      location: 'Miami, FL',
      rating: 5,
      review: 'The pet care section was exactly what I needed. Being able to set up care instructions and funding for my two dogs gives me incredible peace of mind. The interface is intuitive and professional.',
      verified: true
    },
    {
      id: 5,
      name: 'Amanda Foster',
      location: 'Phoenix, AZ',
      rating: 4,
      review: 'Great service overall. The document generation was quick and the legal review option gave me confidence. Only wish there were more customization options for complex estates, but perfect for most people.',
      verified: true
    },
    {
      id: 6,
      name: 'David Kumar',
      location: 'Chicago, IL',
      rating: 5,
      review: 'Life Saver helped my wife and I create matching wills in under an hour. The asset distribution tools made it easy to ensure our children inherit everything fairly. Highly recommend to any parent.',
      verified: true
    }
  ];

  return (
    <section className="reviews-section">
      <div className="container">
        <div className="section-header" style={{ flexDirection: 'column' }}>
          <h2 className="screen-title">
            Trusted by Thousands of Families
          </h2>
          <p className="section-subtitle">
            Join over 50,000 people who have secured their family's future with Life Saver
          </p>
          <div className="rating-display">
            <div className="stars">
              {[ 1, 2, 3, 4, 5 ].map((star) => (
                <Star key={star} className="star star-filled" />
              ))}
            </div>
            <span className="rating-score">4.9/5</span>
            <span className="rating-count">(2,847 reviews)</span>
          </div>
        </div>

        <div className="reviews-grid">
          {reviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>

        <div className="trust-badge">
          <div className="badge">
            <Shield className="badge-icon" />
            <span>All reviews verified by TrustPilot</span>
          </div>
        </div>
      </div>
    </section>
  );
};

const ReviewCard = ({ review }) => (
  <div className="review-card">
    <div className="review-header">
      <div className="review-stars">
        {[ 1, 2, 3, 4, 5 ].map((star) => (
          <Star 
            key={star} 
            className={`star ${star <= review.rating ? 'star-filled' : 'star-empty'}`} 
          />
        ))}
      </div>
      {review.verified && (
        <span className="verified-badge">
          <Check className="verified-icon" />
          Verified
        </span>
      )}
    </div>
    
    <p className="review-text">"{review.review}"</p>
    
    <div className="review-footer">
      <div className="reviewer-info">
        <div className="reviewer-name">{review.name}</div>
        <div className="reviewer-location">{review.location}</div>
      </div>
    </div>
  </div>
);

// Pricing Plans Section
const PricingPlans = () => {
  const plans = [
    {
      name: 'Essential',
      price: 9.99,
      description: 'Perfect for singles and couples just starting out',
      features: [
        'Basic will creation',
        'Beneficiary management',
        'Asset distribution',
        'Digital document storage',
        'Email support',
        'Legal compliance check'
      ],
      popular: false,
      buttonText: 'Start Essential'
    },
    {
      name: 'Complete',
      price: 14.99,
      description: 'Comprehensive protection for families',
      features: [
        'Everything in Essential',
        'Pet care arrangements',
        'Guardian selection for minors',
        'Debt forgiveness options',
        'Advanced directives',
        'Priority support',
        'Legal review option',
        'Unlimited updates'
      ],
      popular: true,
      buttonText: 'Start Complete'
    },
    {
      name: 'Premium',
      price: 19.99,
      description: 'Full estate planning suite with expert guidance',
      features: [
        'Everything in Complete',
        'Trust creation assistance',
        'Tax optimization guidance',
        'Business succession planning',
        '1-on-1 expert consultation',
        'Phone support',
        'Expedited legal review',
        'Family sharing (up to 4 members)'
      ],
      popular: false,
      buttonText: 'Start Premium'
    }
  ];

  return (
    <section className="pricing-section">
      <div className="container">
        <div className="section-header">
          <h2 className="screen-title">
            Simple, Transparent Pricing
          </h2>
          <p className="section-subtitle">
            Choose the plan that fits your needs. All plans include unlimited revisions and secure document storage.
          </p>
        </div>

        <div className="pricing-grid">
          {plans.map((plan) => (
            <PricingCard key={plan.name} plan={plan} />
          ))}
        </div>

        <div className="pricing-footer">
          <p className="guarantee-text">
            All plans include a 30-day money-back guarantee
          </p>
          <div className="features-list">
            <div className="feature-item">
              <Shield className="feature-icon" />
              Bank-level security
            </div>
            <div className="feature-item">
              <FileText className="feature-icon" />
              Legally valid in all 50 states
            </div>
            <div className="feature-item">
              <Users className="feature-icon" />
              Expert legal review available
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const PricingCard = ({ plan }) => (
  <div className={`pricing-card ${plan.popular ? 'pricing-card-popular' : ''}`}>
    {plan.popular && (
      <div className="popular-badge">
        <span>Most Popular</span>
      </div>
    )}
    
    <div className="pricing-header">
      <h3 className="plan-name">{plan.name}</h3>
      <p className="plan-description">{plan.description}</p>
      <div className="plan-price">
        <span className="price">${plan.price}</span>
        <span className="price-period">one-time</span>
      </div>
    </div>

    <ul className="features-list">
      {plan.features.map((feature, index) => (
        <li key={index} className="feature-item">
          <Check className="feature-check" />
          <span>{feature}</span>
        </li>
      ))}
    </ul>

    <button className={`pricing-button ${plan.popular ? 'pricing-button-popular' : ''}`}>
      {plan.buttonText}
    </button>
  </div>
);

// FAQ Section
const FAQSection = () => {
  const [ openFAQ, setOpenFAQ ] = useState(null);

  const faqs = [
    {
      id: 1,
      question: 'Are the wills created through Life Saver legally valid?',
      answer: 'Yes, absolutely. All wills created through Life Saver meet the legal requirements for all 50 states. Our documents are reviewed by licensed attorneys and follow state-specific laws. We also offer optional legal review by certified estate planning attorneys for additional peace of mind.'
    },
    {
      id: 2,
      question: 'How long does it take to create a will?',
      answer: 'Most people complete their will in 15-30 minutes. The exact time depends on the complexity of your estate and how many assets and beneficiaries you have. You can save your progress at any time and return to finish later.'
    },
    {
      id: 3,
      question: 'What happens to my documents if I need to make changes?',
      answer: 'You can update your will anytime through your Life Saver account. All plans include unlimited revisions. When you make changes, we automatically generate a new version and archive the previous one. You\'ll always have access to your complete document history.'
    },
    {
      id: 4,
      question: 'Is my personal information secure?',
      answer: 'Your privacy and security are our top priorities. We use bank-level 256-bit SSL encryption for all data transmission and storage. Your information is stored on secure servers with multiple redundancies. We never share your personal information with third parties and you maintain complete control over your data.'
    },
    {
      id: 5,
      question: 'Do I need to notarize my will?',
      answer: 'Notarization requirements vary by state. Life Saver automatically tells you what\'s required in your state and guides you through the signing process. Some states require witnesses, others require notarization, and some require both. We provide clear instructions specific to your location.'
    },
    {
      id: 6,
      question: 'What if I have a complex estate or business interests?',
      answer: 'Life Saver handles most estate planning needs, including business assets, multiple properties, and complex beneficiary arrangements. For very complex estates (over $1M or involving trusts), our Premium plan includes consultation with certified estate planning attorneys who can provide personalized guidance.'
    },
    {
      id: 7,
      question: 'Can I include my pets in my will?',
      answer: 'Yes! Life Saver includes comprehensive pet care planning. You can designate caretakers, set aside funds for their care, provide detailed care instructions, and even include backup caretakers. This ensures your beloved pets are properly cared for according to your wishes.'
    },
    {
      id: 8,
      question: 'What\'s the difference between the pricing plans?',
      answer: 'Essential covers basic will creation and asset distribution. Complete adds pet care, guardianship, and advanced directives - perfect for families. Premium includes everything plus business planning, tax guidance, and expert consultations. All plans include unlimited updates and secure storage.'
    },
    {
      id: 9,
      question: 'How do I share my will with family members?',
      answer: 'Life Saver provides secure sharing options. You can grant access to specific family members or your executor, send secure links to your documents, or simply let them know where to find your physical copies. We also offer digital legacy planning to ensure your family can access important information when needed.'
    },
    {
      id: 10,
      question: 'What if I need help during the process?',
      answer: 'We\'re here to help every step of the way. Essential plan includes email support, Complete plan adds priority support, and Premium plan includes phone support plus 1-on-1 expert consultation. Our comprehensive help center also has detailed guides and video tutorials.'
    }
  ];

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <section className="faq-section">
      <div className="container">
        <div className="section-header">
          <h2 className="screen-title">
            Frequently Asked Questions
          </h2>
          <p className="section-subtitle">
            Everything you need to know about creating your will with Life Saver
          </p>
        </div>

        <div className="faq-list">
          {faqs.map((faq) => (
            <FAQItem 
              key={faq.id} 
              faq={faq} 
              isOpen={openFAQ === faq.id}
              onToggle={() => toggleFAQ(faq.id)}
            />
          ))}
        </div>

        <div className="faq-footer">
          <p className="contact-text">
            Still have questions? We're here to help.
          </p>
          <button className="contact-button">
            <Heart className="contact-icon" />
            Contact Support
          </button>
        </div>
      </div>
    </section>
  );
};

const FAQItem = ({ faq, isOpen, onToggle }) => (
  <div className="faq-item">
    <button className="faq-question" onClick={onToggle}>
      <span className="question-text">{faq.question}</span>
      {isOpen ? (
        <ChevronUp className="chevron-icon" />
      ) : (
        <ChevronDown className="chevron-icon" />
      )}
    </button>
    
    {isOpen && (
      <div className="faq-answer">
        <p>{faq.answer}</p>
      </div>
    )}
  </div>
);

// star icon
const Star = ({ className }) => (
  <span className={`material-symbols-outlined ${className}`}>
      star
  </span>
);  

const Shield = ({ className }) => (
  <span className={`material-symbols-outlined ${className}`}>
      shield
  </span>
);

const Check = ({ className }) => (
  <span className={`material-symbols-outlined ${className}`}>
      check
  </span>
);

const FileText = ({ className }) => (
  <span className={`material-symbols-outlined ${className}`}>
      article
  </span>
);

const Users = ({ className }) => (
  <span className={`material-symbols-outlined ${className}`}>
      group
  </span>
);

const Heart = ({ className }) => (
  <span className={`material-symbols-outlined ${className}`}>
      favorite
  </span>
);

const ChevronDown = ({ className }) => (
  <span className={`material-symbols-outlined ${className}`}>
      keyboard_arrow_down
  </span>
);

const ChevronUp = ({ className }) => (
  <span className={`material-symbols-outlined ${className}`}>
      keyboard_arrow_up
  </span>
);

export default LandingPageSections;